import axios from 'axios';
import i18n from '../i18n';

// For development hybrid mode: Frontend uses HTTPS (for camera), backend must also use HTTPS
// to avoid mixed content blocking by the browser

// Use the development proxy so mobile browsers need to trust only the UI origin.
const API_URL = process.env.REACT_APP_API_URL || '/api';
const AZURE_IMAGE_BASE_URL = (process.env.REACT_APP_AZURE_IMAGE_BASE_URL || '').replace(/\/$/, '');

export const buildAzureImageUrl = (webRefNo, versionPath, photoType) =>
  AZURE_IMAGE_BASE_URL
    ? `${AZURE_IMAGE_BASE_URL}/${webRefNo}/${versionPath}/${photoType}.jpg`
    : '';

// Debug log to check what URL is being used
console.log('API URL being used:', API_URL);

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  },
  // Allow slower mobile and first-load requests without failing prematurely.
  timeout: 30000,
  withCredentials: true
});

// Log requests for debugging
api.interceptors.request.use(request => {
  console.log('API Request:', request.method, request.url);
  return request;
});

export const isUnauthorized = error => error?.response?.status === 401;

let refreshRequest;
api.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config || {};
    const authPath = String(original.url || '');
    const isAuthCall = ['/auth/login', '/auth/refresh', '/auth/forgot-password', '/auth/reset-password']
      .some(path => authPath.includes(path));
    if (isUnauthorized(error) && localStorage.getItem('user') && !original._retried && !isAuthCall) {
      original._retried = true;
      try {
        refreshRequest ||= api.post('/auth/refresh', {}, { _retried: true });
        await refreshRequest;
        refreshRequest = null;
        return api(original);
      } catch (refreshError) {
        refreshRequest = null;
        localStorage.removeItem('user');
        window.dispatchEvent(new CustomEvent('auth-session-expired', { detail: refreshError.response?.data?.message }));
      }
    }
    return Promise.reject(error);
  }
);

// Authentication services
export const authService = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },

  logout: async () => {
    await api.post('/auth/logout');
  },

  logoutAll: async () => {
    await api.post('/auth/logout-all');
  },

  refresh: async () => {
    const response = await api.post('/auth/refresh');
    return response.data;
  },

  forgotPassword: async email => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },

  resetPassword: async (token, password) => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

export const serviceLogService = {
  getLogs: async params => {
    const response = await api.get('/service-logs', { params });
    if (response.data?.status !== 'success') {
      throw new Error(response.data?.message || 'Failed to load service logs');
    }
    return response.data.data;
  },
};

export const userService = {
  list: async () => (await api.get('/users')).data.data,
  create: async user => (await api.post('/users', user)).data.data,
};

// Language services
export const languageService = {
  getLanguages: async () => {
    const response = await api.get('/languages');
    return response.data;
  }
};

// Case services
export const caseService = {
  getCreationMasters: async () => {
    const response = await api.get('/CaseCreationServices/Masters');
    return response.data;
  },

  createFromLegacyForm: async (caseData) => {
    const response = await api.post('/CaseCreationServices/CreateCase', caseData);
    return response.data;
  },

  getAllCases: async () => {
    const lang = i18n.language || 'en';
    const response = await api.get(`/cases?lang=${lang}`);
    return response.data;
  },

  getCasesByStatus: async (status) => {
    const lang = i18n.language || 'en';
    const response = await api.get(`/cases/status/${status}?lang=${lang}`);
    return response.data;
  },

  getCaseById: async (id) => {
    const lang = i18n.language || 'en';
    const response = await api.get(`/cases/${id}?lang=${lang}`);
    return response.data;
  },

  getCaseByRefNo: async (refNo) => {
    const lang = i18n.language || 'en';
    const response = await api.get(`/cases/reference/${refNo}?lang=${lang}`);
    return response.data;
  },

  getCaseHistory: async (refNo) => {
    const response = await api.get(`/cases/reference/${refNo}/history`);
    return response.data;
  },

  createCase: async (caseData) => {
    const response = await api.post('/cases', caseData);
    return response.data;
  },

  updateCase: async (id, caseData) => {
    const response = await api.put(`/cases/${id}`, caseData);
    return response.data;
  },

  updateCaseStatus: async (id, status) => {
    const response = await api.patch(`/cases/${id}/status`, { status });
    return response.data;
  },

  getCaseCounts: async () => {
    const response = await api.get('/cases/counts');
    return response.data;
  },

  // Get only reassigned images with the latest maximum version for a specific web reference number
  getReassignedImages: async (webRefNo) => {
    try {
      console.log('API Service: Fetching reassigned images for case reference:', webRefNo);

      // Make a specific API call to get only reassigned images with max version
      const response = await api.get(`/cases/${webRefNo}/reassigned-images`);

      console.log('API Service: Reassigned images response:', {
        status: response.status,
        hasData: !!response.data,
        isArray: Array.isArray(response.data),
        dataLength: Array.isArray(response.data) ? response.data.length :
          (response.data && response.data.data && Array.isArray(response.data.data)) ?
            response.data.data.length : 'N/A'
      });

      // Handle response format
      let imageData;

      if (Array.isArray(response.data)) {
        // Direct array format
        imageData = response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        // Nested data property format
        imageData = response.data.data;
      } else {
        // Fallback
        imageData = response.data || [];
      }

      // Log sample of what we're returning
      console.log('API Service: Reassigned image data sample:',
        Array.isArray(imageData) && imageData.length > 0 ?
          [imageData[0], imageData.length > 1 ? imageData[1] : null] :
          'No reassigned image data found');

      // Return a standardized response format
      return {
        status: 'success',
        data: imageData || [],
      };
    } catch (error) {
      console.error('API Service: Error fetching reassigned images:', error);
      return { status: 'error', message: 'Failed to fetch reassigned images', data: [] };
    }
  },

  sendInspectionEmail: async (caseId, email) => {
    const response = await api.post(`/cases/${caseId}/send-email`, { email });
    return response.data;
  },

  // Function to reassign case images
  reassignCaseImages: async (caseReference, reassignmentData) => {
    try {
      console.log('API Service: Reassigning images for case:', caseReference);

      const response = await api.post(`/cases/${caseReference}/reassign`, reassignmentData, {
        timeout: 20000,
        headers: {
          'Content-Type': 'application/json',
          'X-Request-Type': 'CaseReassignment'
        }
      });

      console.log('API Service: Reassignment response status:', response.status);
      console.log('API Service: Reassignment response data:', response.data);

      return response.data;
    } catch (error) {
      console.error('API Service: Error reassigning case images:', error);
      throw error;
    }
  },

  // Function to save individual image remark
  saveImageRemark: async (caseReference, remarkData) => {
    try {
      console.log('API Service: Saving image remark for case:', caseReference);

      const response = await api.post(`/cases/${caseReference}/image-remark`, remarkData, {
        timeout: 10000, // 10 seconds timeout
        headers: {
          'Content-Type': 'application/json',
          'X-Request-Type': 'ImageRemark'
        }
      });

      console.log('API Service: Image remark response status:', response.status);
      console.log('API Service: Image remark response data:', response.data);

      return response.data;
    } catch (error) {
      console.error('API Service: Error saving image remark:', error);
      throw error;
    }
  },

  // Function to update reassigned image URLs directly in the database
  updateReassignedImageUrls: async (webRefNo, photoType, versionNumber, azureUrl, fileUrl) => {
    try {
      console.log('API Service: Directly updating reassigned image URLs in database');
      console.log('API Service: WebRefNo:', webRefNo, 'PhotoType:', photoType, 'Version:', versionNumber);
      console.log('API Service: Azure URL:', azureUrl);
      console.log('API Service: File URL:', fileUrl);

      // Create a simple payload with just the URL information
      const payload = {
        webRefNo,
        photoType,
        version: versionNumber,
        azureUrl,
        fileUrl,
        updateType: 'direct_url_update',
        timestamp: new Date().toISOString()
      };

      // Use a dedicated endpoint for direct URL updates
      const response = await api.post(`/cases/update-image-urls`, payload, {
        headers: { 'Content-Type': 'application/json' }
      });

      console.log('API Service: Direct URL update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('API Service: Error updating reassigned image URLs:', error.message);
      throw error;
    }
  },

  // Function to sync reassigned images with the database
  syncReassignedImage: async (webRefNo, imageData) => {
    try {
      console.log('API Service: Syncing reassigned image for:', webRefNo, 'type:', imageData.id);
      console.log('API Service: Image data length:', imageData.data ? imageData.data.length : 'No data');

      // Save image data to local storage first for offline access
      try {
        localStorage.setItem(`image_${webRefNo}_${imageData.id}`, imageData.data);
        console.log('API Service: Reassigned image saved to local storage');
      } catch (storageError) {
        console.warn('API Service: Failed to save to local storage, may be too large:', storageError);
      }

      // Get the max version from the case data if available
      const maxVersionFromDb = localStorage.getItem(`${webRefNo}_max_version`) || '2';

      // Use the max version from the database
      const versionNumber = maxVersionFromDb;
      const versionPath = `V${versionNumber}`;

      console.log(`API Service: Using version ${versionPath} for syncing reassigned image`);

      // Normalize the photo type to ensure consistent file naming
      const normalizedPhotoType = imageData.id.toLowerCase().replace(/ /g, '_');

      // Create the expected URLs with version path
      const azureUrl = buildAzureImageUrl(webRefNo, versionPath, normalizedPhotoType);
      const fileUrl = `/uploads/${webRefNo}/${versionPath}/${normalizedPhotoType}.jpg`;

      // Store the formatted Azure URL in localStorage for reference
      localStorage.setItem(`azure_image_${webRefNo}_${imageData.id}`, azureUrl);
      localStorage.setItem(`azure_image_${webRefNo}_${imageData.id}_version`, versionPath);
      localStorage.setItem(`azure_image_${webRefNo}_${imageData.id}_version_number`, versionNumber);

      console.log('API Service: Azure URL saved to local storage:', azureUrl, 'with version', versionPath);

      // Prepare the payload specifically for reassigned images
      const payload = {
        webRefNo,
        photoType: normalizedPhotoType,
        imageData: imageData.data,
        version: versionNumber,
        azureUrl: azureUrl,
        fileUrl: fileUrl,
        metadata: {
          latitude: imageData.latitude || null,
          longitude: imageData.longitude || null,
          capturedAt: imageData.timestamp || new Date().toISOString(),
          capturedBy: localStorage.getItem('username') || 'anonymous'
        }
      };

      // Use the existing sync-image endpoint with query parameters
      const syncEndpoint = `/cases/sync-image`;

      // Add specific query parameters to target the exact version
      const syncEndpointWithVersion = `${syncEndpoint}?version=${versionNumber}&photoType=${normalizedPhotoType}&webRefNo=${webRefNo}`;
      console.log('API Service: Using endpoint for reassigned images:', syncEndpointWithVersion);

      // Add version information to the payload
      payload.targetVersion = versionNumber;
      payload.isReassigned = true;
      payload.updateExisting = true;
      payload.expectedAzureUrl = azureUrl;
      payload.expectedFileUrl = fileUrl;

      const response = await api.post(syncEndpointWithVersion, payload, {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('API Service: Reassigned image sync response:', response.data);
      return response.data;
    } catch (error) {
      console.error('API Service: Error syncing reassigned image:', error);
      console.error('API Service: No response received, request was:', error.request);
      throw error;
    }
  },

  // Function to sync captured images with the database
  syncImage: async (webRefNo, imageData) => {
    try {
      console.log('API Service: Syncing image for:', webRefNo, 'type:', imageData.id);
      console.log('API Service: Image data length:', imageData.data ? imageData.data.length : 'No data');

      // Normal inspections use V1; reassignment explicitly supplies V2, V3, ...
      const versionNumber = String(imageData.version || '1');
      const versionPath = `V${versionNumber}`;

      console.log(`API Service: Using version ${versionPath} for syncing reassigned image`);

      // Normalize the photo type to ensure consistent file naming
      // This ensures we use the same format as the original database entries
      const normalizedPhotoType = imageData.id.toLowerCase().replace(/ /g, '_');

      // Keep the JSON contract for broad browser support, but send only fields
      // consumed by the API.
      const payload = {
        webRefNo,
        photoType: normalizedPhotoType,
        imageData: imageData.data,
        aiImageData: imageData.aiData,
        version: versionNumber,
        metadata: {
          latitude: imageData.latitude || null,
          longitude: imageData.longitude || null,
          capturedAt: imageData.timestamp || new Date().toISOString(),
          capturedBy: localStorage.getItem('username') || 'anonymous'
        }
      };

      console.log('API Service: Sending payload to endpoint /cases/sync-image');
      console.log('API Service: Using version:', versionNumber, 'for photo type:', normalizedPhotoType);
      console.log('API Service: Image payload size (bytes):', imageData.data.length);

      // Use the standard sync-image endpoint that already exists on the backend
      const syncEndpoint = `/cases/sync-image`;

      // Add specific query parameters to target the exact version
      const syncEndpointWithVersion = `${syncEndpoint}?version=${versionNumber}&photoType=${normalizedPhotoType}&webRefNo=${webRefNo}`;
      console.log('API Service: Using endpoint for reassigned images:', syncEndpointWithVersion);

      // Set longer timeout for large image uploads
      const response = await api.post(syncEndpointWithVersion, payload, {
        timeout: 30000, // 30 seconds timeout for image upload
        headers: {
          'Content-Type': 'application/json'
          // Removed custom headers that were causing CORS issues
        }
      });

      console.log('API Service: Sync response status:', response.status);
      console.log('API Service: Sync response data:', response.data);

      if (response.data?.status !== 'success' || !response.data?.data?.azureUrl) {
        throw new Error('Cloud upload was not confirmed');
      }

      // Store Azure URL in local storage if available
      if (response.data?.data?.azureUrl) {
        try {
          // Get the original Azure URL from the response
          const originalAzureUrl = response.data.data.azureUrl;

          // For reassigned images, use the max version from the database
          const version = versionPath; // This comes from the earlier part of the function

          // Get the normalized photo type that was used in the request
          const normalizedPhotoType = payload.photoType;
          const originalPhotoType = imageData.id;

          console.log(`API Service: Processing Azure URL for ${normalizedPhotoType} (original: ${originalPhotoType})`);

          // Format the Azure URL to include the version path
          let formattedUrl = originalAzureUrl;

          // Check if URL already contains version pattern
          if (!originalAzureUrl.match(/\/V[0-9]\//i)) {
            // Extract the container and reference number parts
            const urlParts = originalAzureUrl.split('/');

            // Find the container and reference number parts
            let containerPart = '';
            let refNoIndex = -1;

            for (let i = 0; i < urlParts.length; i++) {
              if (urlParts[i] === webRefNo || urlParts[i].includes(webRefNo)) {
                refNoIndex = i;
                containerPart = urlParts.slice(0, i).join('/');
                break;
              }
            }

            if (refNoIndex !== -1) {
              // Use the normalized photo type for the filename to maintain consistency
              // This ensures the filename matches the original format in the database
              const filename = `${normalizedPhotoType}.jpg`;

              // Construct the new URL with version path and consistent filename
              formattedUrl = `${containerPart}/${webRefNo}/${version}/${filename}`;
              console.log('API Service: Formatted Azure URL with version path and consistent filename:', formattedUrl);
            } else {
              console.log('API Service: Could not find reference number in URL, using original format');
            }
          } else {
            console.log('API Service: URL already contains version path');
          }

          // Store both the original and formatted URLs
          localStorage.setItem(`azure_image_${webRefNo}_${originalPhotoType}_original`, originalAzureUrl);
          localStorage.setItem(`azure_image_${webRefNo}_${originalPhotoType}`, formattedUrl);

          // Store the version information - using the dynamic version from the database
          localStorage.setItem(`azure_image_${webRefNo}_${originalPhotoType}_version`, version);
          localStorage.setItem(`azure_image_${webRefNo}_${originalPhotoType}_version_number`, versionNumber);

          // Also store the max version for this case for future reference
          localStorage.setItem(`${webRefNo}_max_version`, versionNumber);

          console.log(`API Service: Azure URL saved to local storage: ${formattedUrl} with version ${version}`);

          // Return the formatted URL in the response for immediate use
          response.data.data.formattedAzureUrl = formattedUrl;
          response.data.data.normalizedPhotoType = normalizedPhotoType;
        } catch (storageError) {
          console.warn('API Service: Failed to save Azure URL to local storage:', storageError);
        }
      }

      return response.data;
    } catch (error) {
      console.error('API Service: Error syncing image:', error.message);
      if (error.response) {
        console.error('API Service: Error response:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('API Service: No response received, request was:', error.request);
      }
      throw error;
    }
  },

  // Get case by web reference number
  getCaseByRefNo: async (webRefNo) => {
    const response = await api.get(`/cases/reference/${webRefNo}`);
    return response.data;
  },

  // Get case images
  getCaseImages: async (webRefNo) => {
    const response = await api.get(`/cases/images/${webRefNo}`);
    return response;
  },

  // Case locking functions
  lockCase: async (webRefNo, username) => {
    const response = await api.post(`/cases/${webRefNo}/lock`, { username });
    return response.data;
  },

  unlockCase: async (webRefNo, username) => {
    const response = await api.post(`/cases/${webRefNo}/unlock`, { username });
    return response.data;
  },

  checkCaseLock: async (webRefNo) => {
    const response = await api.get(`/cases/${webRefNo}/lock-status`);
    return response.data;
  },

  // Get the maximum version for a case from the database
  getMaxVersion: async (webRefNo) => {
    try {
      console.log('API Service: Getting max version for case:', webRefNo);

      const response = await api.get(`/cases/${webRefNo}/max-version`);

      console.log('API Service: Max version response:', response.data);

      // Store the max version in localStorage for future use
      if (response.data && response.data.maxVersion) {
        localStorage.setItem(`${webRefNo}_max_version`, response.data.maxVersion);
        console.log(`API Service: Stored max version ${response.data.maxVersion} for ${webRefNo}`);
      }

      return response.data;
    } catch (error) {
      console.error('API Service: Error getting max version:', error.message);
      if (error.response) {
        console.error('API Service: Error response:', error.response.status, error.response.data);
      }
      // Default to version 1 if we can't get the max version
      return { maxVersion: '1' };
    }
  },

  // Delete an image
  deleteImage: async (webRefNo, photoType, version) => {
    try {
      console.log(`API Service: Deleting image for ${webRefNo}, photo type: ${photoType}, version: ${version}`);

      // Call the delete-image endpoint with query parameters
      const response = await api.delete(`/cases/delete-image?webRefNo=${webRefNo}&photoType=${photoType}&version=${version}`);

      console.log('API Service: Delete image response:', response.data);
      return response.data;
    } catch (error) {
      console.error('API Service: Error deleting image:', error.message);
      throw error;
    }
  },

  // Get reassigned images with cache-busting parameter
  getReassignedImages: async (webRefNo, timestamp = null) => {
    try {
      console.log(`API Service: Getting reassigned images for ${webRefNo}`);

      // Add cache-busting parameter if provided
      const cacheBuster = timestamp ? `?cache=${timestamp}` : '';

      // Call the API endpoint to get reassigned images
      const response = await api.get(`/cases/${webRefNo}/reassigned-images${cacheBuster}`);

      console.log('API Service: Reassigned images response:', response.data);
      return response.data;
    } catch (error) {
      console.error('API Service: Error getting reassigned images:', error.message);
      throw error;
    }
  },

  // Submit reassigned images to finalize the inspection
  submitReassignedImages: async (webRefNo) => {
    try {
      console.log('API Service: Submitting reassigned images for:', webRefNo);

      // First, get the latest max version from the server
      const maxVersionResponse = await api.get(`/cases/${webRefNo}/max-version`);
      const serverMaxVersion = maxVersionResponse.data.maxVersion ? parseInt(maxVersionResponse.data.maxVersion) : 2;

      console.log(`API Service: Got max version from server: ${serverMaxVersion}`);

      // Update localStorage with the server's max version
      localStorage.setItem(`${webRefNo}_max_version`, serverMaxVersion.toString());

      const reassignmentVersion = serverMaxVersion;
      console.log(`API Service: Submitting reassignment version: ${reassignmentVersion}`);

      // Get all image keys from localStorage for this reference number
      const imageKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key.startsWith(`azure_image_${webRefNo}_`) && !key.includes('_version') && !key.includes('_original')) {
          imageKeys.push(key);
        }
      }

      // Extract photo types and URLs
      const images = imageKeys.map(key => {
        const photoType = key.replace(`azure_image_${webRefNo}_`, '');
        const imageUrl = localStorage.getItem(key);
        const version = `V${reassignmentVersion}`;

        return {
          photoType,
          imageUrl,
          version
        };
      });

      console.log('API Service: Submitting images:', images);

      // Submit to API
      const response = await api.post(`/cases/${webRefNo}/submit-reassigned`, {
        webRefNo,
        images,
        submittedBy: JSON.parse(localStorage.getItem('user') || 'null')?.username || 'Customer',
        submittedAt: new Date().toISOString()
      });

      console.log('API Service: Submit response:', response.data);
      return response.data;
    } catch (error) {
      console.error('API Service: Error submitting reassigned images:', error.message);
      if (error.response) {
        console.error('API Service: Error response:', error.response.status, error.response.data);
      } else if (error.request) {
        console.error('API Service: No response received, request was:', error.request);
      }
      throw error;
    }
  },

  // Update case status
  updateCaseStatus: async (webRefNo, data) => {
    try {
      console.log('API Service: Updating case status for reference:', webRefNo);
      console.log('API Service: Update data:', JSON.stringify(data));

      // Ensure we have valid data
      if (!webRefNo) {
        console.error('API Service: Missing web reference number');
        throw new Error('Missing web reference number');
      }

      if (!data || !data.status) {
        console.error('API Service: Missing status in update data');
        throw new Error('Missing status in update data');
      }

      // Prepare the request payload
      const payload = {
        status: data.status,
        remarks: data.remarks || '',
        type: data.type || '',
        updatedBy: data.updatedBy || JSON.parse(localStorage.getItem('user') || 'null')?.username || 'Customer'
      };

      console.log('API Service: Sending payload:', JSON.stringify(payload));

      const response = await api.patch(`/cases/${webRefNo}/status`, payload);
      console.log('API Service: Update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('API Service: Error updating case status:', error);
      console.error('API Service: Error details:', error.response ? error.response.data : 'No response data');
      throw error;
    }
  },

  // Get case counts by status
  getCaseCounts: async () => {
    try {
      console.log('API Service: Fetching case counts');
      const response = await api.get('/cases/counts');
      console.log('API Service: Case counts response:', response.data);
      return response.data;
    } catch (error) {
      console.error('API Service: Error fetching case counts:', error);
      throw error;
    }
  },

  // Generate PDF report for a case
  generatePdfReport: async (webRefNo, syncWithAzure = false) => {
    try {
      console.log('API Service: Generating PDF report for case:', webRefNo, 'syncWithAzure:', syncWithAzure);

      if (syncWithAzure) {
        // If syncing with Azure, make a regular API call and return the Azure URL
        const response = await api.get(`/reports/${webRefNo}/generate-pdf?syncWithAzure=true`);
        console.log('API Service: PDF report generation response:', response.data);

        return {
          status: 'success',
          message: 'PDF report generated and uploaded to Azure',
          data: response.data.data
        };
      } else {
        const lang = localStorage.getItem('i18nextLng') || 'en';
        const response = await api.get(`/reports/${webRefNo}/generate-pdf?lang=${lang}`, {
          responseType: 'blob'
        });
        return response.data;
      }
    } catch (error) {
      console.error('API Service: Error generating PDF report:', error);
      return { status: 'error', message: 'Failed to generate PDF report' };
    }
  },

  generateAiPdfReport: async (webRefNo) => {
    const lang = localStorage.getItem('i18nextLng') || 'en';
    const response = await api.get(`/reports/${webRefNo}/generate-ai-pdf?lang=${lang}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Get all images for a case
  getCaseImages: async (webRefNo) => {
    try {
      console.log('API Service: Fetching images for case reference:', webRefNo);

      // Try the standard endpoint first
      const response = await api.get(`/cases/${webRefNo}/images`);
      console.log('API Service: Images response structure:', {
        status: response.status,
        hasData: !!response.data,
        isArray: Array.isArray(response.data),
        hasDataProperty: response.data && response.data.data && Array.isArray(response.data.data),
        dataLength: Array.isArray(response.data) ? response.data.length :
          (response.data && response.data.data && Array.isArray(response.data.data)) ?
            response.data.data.length : 'N/A'
      });

      // If we got a successful response, return it
      if (response.data) {
        let imageData;
        let hasReassignedImages = false;
        let reassignedImages = [];

        // Handle both old and new response formats
        if (Array.isArray(response.data)) {
          // New format - direct array
          imageData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          // Old format - data property
          imageData = response.data.data;

          // Check if there are reassigned images in the response
          if (response.data.reassignedImages && Array.isArray(response.data.reassignedImages)) {
            hasReassignedImages = true;
            reassignedImages = response.data.reassignedImages;
          }
        } else {
          // Fallback
          imageData = response.data;
        }

        // Log what we're returning
        console.log('API Service: Image data sample:',
          Array.isArray(imageData) && imageData.length > 0 ?
            [imageData[0], imageData.length > 1 ? imageData[1] : null] :
            'No image data found');

        // Return a standardized response format
        return {
          status: 'success',
          data: imageData || [],
          hasReassignedImages: hasReassignedImages,
          reassignedImages: reassignedImages,
          caseStatus: response.data.caseStatus || 'Unknown'
        };
      }

      // If we didn't get data, try an alternative endpoint format
      console.log('API Service: Trying alternative endpoint');
      const altResponse = await api.get(`/cases/images/${webRefNo}`);
      console.log('API Service: Alternative response structure:', {
        status: altResponse.status,
        hasData: !!altResponse.data,
        isArray: Array.isArray(altResponse.data),
        hasDataProperty: altResponse.data && altResponse.data.data && Array.isArray(altResponse.data.data)
      });

      // Return a standardized response format
      return {
        status: 'success',
        data: altResponse.data || [],
        hasReassignedImages: false,
        reassignedImages: []
      };
    } catch (error) {
      console.error('API Service: Error fetching case images:', error);
      if (error.response) {
        console.error('API Service: Error response:', error.response.status, error.response.data);
      }
      // Return standardized error response
      return {
        status: 'error',
        message: 'Failed to fetch case images',
        error: error.message,
        data: []
      };
    }
  },

  // Function to sync images from Azure Blob Storage to local storage
  syncImagesFromAzure: async (webRefNo, photoTypes = null) => {
    try {
      console.log('API Service: Syncing images from Azure for:', webRefNo);

      // Prepare the payload
      const payload = {};
      if (photoTypes && Array.isArray(photoTypes) && photoTypes.length > 0) {
        payload.photoTypes = photoTypes;
      }

      // Set longer timeout for potentially large downloads
      const response = await api.post(`/cases/${webRefNo}/sync-from-azure`, payload, {
        timeout: 60000, // 60 seconds timeout for syncing multiple images
        headers: {
          'Content-Type': 'application/json',
          'X-Request-Type': 'AzureSync'
        }
      });

      console.log('API Service: Azure sync response status:', response.status);
      console.log('API Service: Azure sync response data:', response.data);

      // Update local storage with Azure URLs
      if (response.data?.success && response.data?.data?.syncResults) {
        response.data.data.syncResults.forEach(result => {
          if (result.status !== 'failed' && result.azureUrl) {
            try {
              localStorage.setItem(`azure_image_${webRefNo}_${result.photoType}`, result.azureUrl);
              console.log(`API Service: Updated Azure URL in local storage for ${result.photoType}`);
            } catch (storageError) {
              console.warn('API Service: Failed to save Azure URL to local storage:', storageError);
            }
          }
        });
      }

      return response.data;
    } catch (error) {
      console.error('API Service: Error syncing images from Azure:', error);
      throw error;
    }
  },

  getImageMetadata: async (webRefNo, photoType) => {
    try {
      const response = await api.get(`/cases/${webRefNo}/image-metadata/${photoType}`);
      return response.data;
    } catch (error) {
      console.error('Error getting image metadata:', error);
      throw error;
    }
  },

  // Function to get case images with option to include all versions
  getCaseImages: async (webRefNo, includeAllVersions = false) => {
    try {
      console.log(`Getting case images for ${webRefNo}, includeAllVersions=${includeAllVersions}`);

      // Use GET request with allVersions parameter when requested
      // Based on the network tab, the endpoint should be /cases/{webRefNo}/images
      const response = await api.get(`/cases/${webRefNo}/images${includeAllVersions ? '?allVersions=true' : ''}`);
      console.log(`API response for ${webRefNo} images (allVersions=${includeAllVersions}):`, response.data);

      return response.data;
    } catch (error) {
      console.error('Error getting case images:', error);
      throw error;
    }
  },

  // Function to get image version history for a specific photo type
  getImageVersionHistory: async (webRefNo, photoType) => {
    try {
      console.log(`Getting image version history for ${webRefNo}, photoType=${photoType}`);

      // Use GET request to match backend expectations
      const response = await api.get(`/cases/${webRefNo}/image-versions/${photoType}`);

      return response.data;
    } catch (error) {
      console.error('Error getting image version history:', error);
      throw error;
    }
  },

  // Delete an image from the server and update the database
  deleteImage: async (webRefNo, photoType, version = null) => {
    try {
      console.log(`Deleting image for ${webRefNo}, photo type: ${photoType}, version: ${version || 'all versions'}`);

      // Use DELETE request with path parameters to match backend expectations
      let url = `/cases/${webRefNo}/images/${photoType}`;
      if (version) {
        url += `/${version}`;
      }

      const response = await api.delete(url);
      console.log('Delete image response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  },

  // Get image version history for a specific photo type
  getImageVersionHistory: async (webRefNo, photoType) => {
    try {
      const timestamp = new Date().getTime();
      console.log(`Getting version history for ${webRefNo}, photo type: ${photoType}`);

      const response = await api.get(`/cases/${webRefNo}/image-versions/${photoType}?t=${timestamp}`, {
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
          'Expires': '0',
        }
      });

      console.log('Image version history response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error getting image version history:', error);
      throw error;
    }
  },

  // Function to download and cache images locally to avoid CORS issues
  downloadImagesForPdf: async (webRefNo, images) => {
    try {
      console.log('API Service: Downloading images for PDF:', images.length);

      // Create a new array to hold the processed images with local data URLs
      const processedImages = [];

      for (const img of images) {
        try {
          // Skip if no URL
          if (!img.url && !img.azureUrl) {
            console.warn('Image missing URL, skipping:', img);
            continue;
          }

          const imageUrl = img.azureUrl || img.url || img.imageUrl || img.filePath;

          // Check if this is an Azure URL (which will cause CORS issues)
          const isAzureUrl = imageUrl.includes('blob.core.windows.net');

          console.log(`Processing image: ${img.photoType} from ${imageUrl}, isAzureUrl: ${isAzureUrl}`);

          // For Azure URLs, always use the backend proxy
          if (isAzureUrl) {
            try {
              console.log(`Using backend proxy for Azure URL: ${img.photoType}`);

              // Use the same configured API origin as every other request.
              const response = await fetch(`${API_URL}/reports/download-image`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  imageUrl,
                  webRefNo,
                  photoType: img.photoType
                })
              });

              if (!response.ok) {
                throw new Error(`Backend proxy failed with status: ${response.status}`);
              }

              // Get the image blob from the response
              const blob = await response.blob();

              // Convert blob to data URL
              const dataUrl = await new Promise((resolve) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.readAsDataURL(blob);
              });

              // Add the processed image to the array
              processedImages.push({
                ...img,
                url: dataUrl,           // Use data URL for PDF generation
                originalUrl: imageUrl,  // Keep the original URL for reference
                isLocal: true,          // Mark as locally processed
                isDataUrl: true,        // Flag as data URL
                isAzureUrl: true,       // Flag as Azure URL
                azureUrl: null          // Remove the Azure URL to prevent direct access
              });

              console.log(`Successfully processed Azure image via proxy: ${img.photoType}`);
            } catch (proxyError) {
              console.error(`Backend proxy failed for Azure image: ${img.photoType}`, proxyError);

              // Add the original image with a flag indicating it failed
              processedImages.push({
                ...img,
                originalUrl: imageUrl,
                isAzureUrl: true,
                failedToDownload: true,
                error: proxyError.message
              });
            }
          } else {
            // For non-Azure URLs, try multiple approaches
            try {
              console.log(`Processing non-Azure image: ${img.photoType}`);

              // Create a new image element
              const imgElement = new Image();

              // Create a promise that resolves when the image loads
              const dataUrl = await new Promise((resolve, reject) => {
                const timeout = setTimeout(() => {
                  reject(new Error('Image load timeout'));
                }, 15000);

                imgElement.onload = () => {
                  clearTimeout(timeout);

                  try {
                    // Create a canvas to draw the image
                    const canvas = document.createElement('canvas');
                    canvas.width = imgElement.naturalWidth || 800;
                    canvas.height = imgElement.naturalHeight || 600;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(imgElement, 0, 0);

                    // Convert to data URL directly
                    const imgDataUrl = canvas.toDataURL('image/jpeg', 0.9);
                    resolve(imgDataUrl);
                  } catch (canvasError) {
                    console.error('Canvas error:', canvasError);
                    reject(canvasError);
                  }
                };

                imgElement.onerror = (error) => {
                  clearTimeout(timeout);
                  console.error('Image load error:', error);
                  reject(new Error('Failed to load image'));
                };

                // Set crossOrigin attribute and load the image
                imgElement.crossOrigin = 'anonymous';
                imgElement.src = `${imageUrl}?t=${Date.now()}`;
              });

              // Add the processed image to the array
              processedImages.push({
                ...img,
                url: dataUrl,           // Use data URL for PDF generation
                originalUrl: imageUrl,  // Keep the original URL for reference
                isLocal: true,          // Mark as locally processed
                isDataUrl: true         // Flag as data URL
              });

              console.log(`Successfully processed non-Azure image: ${img.photoType}`);
            } catch (error) {
              console.error(`Failed to process non-Azure image: ${img.photoType}`, error);

              // Add the original image with a flag indicating it failed
              processedImages.push({
                ...img,
                originalUrl: imageUrl,
                failedToDownload: true,
                error: error.message
              });
            }
          }
        } catch (imgError) {
          console.error(`Error processing image ${img.photoType}:`, imgError);
          // Add the original image to the array anyway with error flag
          processedImages.push({
            ...img,
            processingError: imgError.message,
            failedToDownload: true
          });
        }
      }

      console.log(`Successfully processed ${processedImages.length} images, with ${processedImages.filter(img => img.isLocal).length} downloaded locally`);
      return processedImages;
    } catch (error) {
      console.error('API Service: Error downloading images for PDF:', error);
      console.error('Stack trace:', error.stack);
      // Return the original images if the download process fails
      return images;
    }
  },
};

// Export the API instance as default
export default api;
